// src/services/dayPlanService.js
// 后端API调用服务 - 独立文件

export class DayPlanService {
  constructor() {
    this.baseURL = 'https://citygateway-booking-backend.vercel.app';
    this.cache = new Map(); // 添加缓存机制
    this.requestTimeout = 10000; // 10秒超时
  }

  async fetchDayPlan(dayNumber, preference = null) {
    const cacheKey = `day_${dayNumber}`;
    
    // 检查缓存
    if (this.cache.has(cacheKey)) {
      console.log(`💾 Using cached data for day ${dayNumber}`);
      return this.cache.get(cacheKey);
    }
  
    try {
      console.log(`🎯 Fetching day plan for day: ${dayNumber}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
  
      // 构造 POST body
      const bodyData = { day: dayNumber };
      if (preference) {
        bodyData.preference = preference;
      }
  
      const response = await fetch(`${this.baseURL}/api/dayplan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Name': 'testDan',
          'X-User-Id': '681a6db71771cb427864f564'
        },
        body: JSON.stringify(bodyData),
        signal: controller.signal
      });
  
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch day plan`);
      }
  
      const data = await response.json();
      
      if (data.error && data.error === 'template not found') {
        console.warn(`⚠️ No template found for day ${dayNumber}`);
        this.cache.set(cacheKey, null); // 缓存null结果避免重复请求
        return null;
      }
  
      // 缓存成功结果
      this.cache.set(cacheKey, data.plan);
      return data.plan;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error(`⏱️ Request timeout for day ${dayNumber}`);
        throw new Error(`Request timeout for day ${dayNumber}`);
      }
      
      console.error(`❌ Error fetching day plan for day ${dayNumber}:`, error);
      throw error;
    }
  }
  

  async fetchMultiDawyPlan(startDay, totalDays) {
    try {
      console.log(`🗓️ Fetching multi-day plan: ${totalDays} days starting from day ${startDay}`);
      
      // 并发请求优化
      const dayPromises = [];
      const maxConcurrent = 3; // 限制并发请求数
      
      for (let i = 0; i < totalDays; i += maxConcurrent) {
        const batch = [];
        for (let j = 0; j < maxConcurrent && (i + j) < totalDays; j++) {
          batch.push(this.fetchDayPlan(startDay + i + j));
        }
        
        const batchResults = await Promise.allSettled(batch);
        dayPromises.push(...batchResults);
      }

      // 处理结果
      const dayPlans = dayPromises.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.error(`Failed to fetch day ${startDay + index}:`, result.reason);
          return null;
        }
      });
      
      // 构建完整计划
      const validPlans = dayPlans
        .map((plan, index) => ({
          dayNumber: startDay + index,
          plan: plan,
          hasData: plan !== null
        }))
        .filter(dayData => dayData.hasData);

      console.log(`✅ Successfully fetched ${validPlans.length}/${totalDays} day plans`);
      
      return {
        totalRequested: totalDays,
        totalReceived: validPlans.length,
        plans: validPlans,
        success: validPlans.length > 0,
        coverage: validPlans.length / totalDays
      };
    } catch (error) {
      console.error('❌ Error fetching multi-day plan:', error);
      throw error;
    }
  }

  // 🆕 智能计划生成 - 当API数据不足时的备用方案
  async generateFallbackPlan(days, preferences = {}) {
    console.log('🔄 Generating fallback plan...');
    
    const fallbackActivities = {
      1: [
        { location: 'Old Montreal Walking Tour', category: 'Sightseeing', start_time: '09:00', end_time: '12:00' },
        { location: 'Notre-Dame Basilica', category: 'Cultural', start_time: '14:00', end_time: '16:00' },
        { location: 'Schwartz\'s Deli', category: 'Dining', start_time: '18:00', end_time: '20:00' }
      ],
      2: [
        { location: 'Mount Royal Park', category: 'Nature', start_time: '09:00', end_time: '12:00' },
        { location: 'Montreal Museum of Fine Arts', category: 'Cultural', start_time: '14:00', end_time: '17:00' },
        { location: 'Plateau Mont-Royal', category: 'Sightseeing', start_time: '19:00', end_time: '21:00' }
      ],
      3: [
        { location: 'Jean-Talon Market', category: 'Food & Culture', start_time: '09:00', end_time: '11:00' },
        { location: 'Olympic Stadium', category: 'Sightseeing', start_time: '13:00', end_time: '16:00' },
        { location: 'Botanical Garden', category: 'Nature', start_time: '17:00', end_time: '19:00' }
      ]
    };

    const plans = [];
    for (let day = 1; day <= days; day++) {
      const dayActivities = fallbackActivities[((day - 1) % 3) + 1] || fallbackActivities[1];
      plans.push({
        dayNumber: day,
        plan: dayActivities.map(activity => ({
          ...activity,
          place_id: `fallback_${day}_${activity.location.replace(/\s+/g, '_').toLowerCase()}`,
          activity_desc: `Explore ${activity.location} - a must-see attraction in Montreal`,
          image_url: `https://picsum.photos/400/300?random=${day}`,
          avg_cost_per_person: 15
        })),
        hasData: true,
        isFallback: true
      });
    }

    return {
      totalRequested: days,
      totalReceived: days,
      plans,
      success: true,
      coverage: 1.0,
      isFallback: true
    };
  }

  // 将API返回的数据转换为应用程序期望的格式
  transformToItineraryFormat(multiDayPlanData, startDate, endDate) {
    if (!multiDayPlanData || !multiDayPlanData.plan) {
      return [];
    }

    const startDateObj = new Date(startDate);
    
    return multiDayPlanData.plan.map((dayData, index) => {
      const currentDate = new Date(startDateObj);
      currentDate.setDate(startDateObj.getDate() + index);
      
      return {
        date: currentDate.toISOString().split('T')[0],
        start_time: `${currentDate.toISOString().split('T')[0]} 09:00`,
        end_time: `${currentDate.toISOString().split('T')[0]} 20:00`,
        plan: dayData.plan || []
      };
    });
  }
}